var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import React, { memo, useState } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { WidgetType } from '@common/api';
import { useUpdateGrid } from '@data/grid/gridActions';
import { useTagSlicersData, useWidgets } from '@data/widgets/widgetActions';
import { WidgetStyled } from '@styled/Widget';
// Widgets
import KPI from './kpi/KPI';
import Chart from './chart/Chart';
import TagSlicer from './tagSlicer/TagSlicer';
import SingleValue from './singleValue/SingleValue';
import TimelineSlicer from './timelineSlicer/TimelineSlicer';
import DataEditor from './dataEditorAndSimulation/DataEditor';
import DataSimulation from './dataEditorAndSimulation/DataSimulation';
import TextTile from './textTile/TextTile';
import Pivot from './pivot/Pivot';
var ResponsiveGridLayout = WidthProvider(Responsive);
var DashGrid = function (_a) {
    var _b;
    var grid = _a.grid, gridLocked = _a.gridLocked, width = _a.width, documentActions = _a.documentActions, saveSlicer = _a.saveSlicer, tagSlicersTagIdsLocalChanges = _a.tagSlicersTagIdsLocalChanges, tagSlicersTagIdsLocalChangesHandler = _a.tagSlicersTagIdsLocalChangesHandler;
    var updateGrid = useUpdateGrid();
    var _c = __read(useState('grid-key-0'), 2), gridKey = _c[0], setGridKey = _c[1];
    var widgetsQuery = useWidgets();
    var widgets = (_b = widgetsQuery.data) === null || _b === void 0 ? void 0 : _b.filter(function (w) { return w.GridId === grid.id; });
    var tagSlicersData = useTagSlicersData(grid.id, tagSlicersTagIdsLocalChanges);
    // used for detecting widgets have changed -> first time load it gets empty array
    var gridParse = {};
    try {
        gridParse = JSON.parse(grid.config || '{}');
    }
    catch (err) {
        gridParse = {};
    }
    var gridConfig = gridParse;
    var reloadGridData = function () {
        setGridKey("grid-key-".concat(Math.floor(Math.random() * 100000)));
    };
    var saveGridLayout = function (newGridLayouts, allGridLayouts) {
        var e_1, _a, e_2, _b;
        if (newGridLayouts.length === 0) {
            return;
        }
        var newGridConfig = __assign(__assign({}, gridConfig), { layouts: {} });
        if (allGridLayouts.lg) {
            newGridConfig.layouts.lg = allGridLayouts.lg;
        }
        if (allGridLayouts.sm) {
            newGridConfig.layouts.sm = allGridLayouts.sm;
        }
        var breakpoints = Object.keys(newGridConfig.layouts);
        try {
            for (var breakpoints_1 = __values(breakpoints), breakpoints_1_1 = breakpoints_1.next(); !breakpoints_1_1.done; breakpoints_1_1 = breakpoints_1.next()) {
                var breakpoint = breakpoints_1_1.value;
                if (breakpoint == 'lg' || breakpoint === 'sm') {
                    try {
                        for (var _c = (e_2 = void 0, __values(newGridConfig.layouts[breakpoint])), _d = _c.next(); !_d.done; _d = _c.next()) {
                            var layout = _d.value;
                            layout.minW = 2;
                            layout.minH = 4;
                            layout.w = layout.w < layout.minW ? layout.minW : layout.w;
                            layout.h = layout.h < layout.minH ? layout.minH : layout.h;
                        }
                    }
                    catch (e_2_1) { e_2 = { error: e_2_1 }; }
                    finally {
                        try {
                            if (_d && !_d.done && (_b = _c.return)) _b.call(_c);
                        }
                        finally { if (e_2) throw e_2.error; }
                    }
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (breakpoints_1_1 && !breakpoints_1_1.done && (_a = breakpoints_1.return)) _a.call(breakpoints_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        var configString = JSON.stringify(newGridConfig);
        if (configString !== grid.config) {
            grid.config = configString;
            updateGrid.mutate(grid);
        }
    };
    if (widgets === null || widgets === void 0 ? void 0 : widgets.length) {
        return (React.createElement(ResponsiveGridLayout, { isDraggable: !gridLocked, isResizable: !gridLocked, className: "layout pins", rowHeight: 20, width: width, breakpoints: { lg: 900, sm: 768 }, layouts: gridConfig.layouts || {}, cols: { lg: 12, sm: 6 }, onLayoutChange: saveGridLayout }, widgets.map(function (w) {
            var widgetKey = "widget-inner-".concat(w.id);
            if (w.type === WidgetType.KPI || w.type === WidgetType.CHART || w.type === WidgetType.SINGLEVALUE) {
                widgetKey = "widget-inner-".concat(w.id, "-").concat(gridKey);
            }
            return (React.createElement(WidgetStyled, { className: "tiny-scrollbars", key: "widget-".concat(w.id) },
                React.createElement(MemoWidgetWrapper, { key: widgetKey, gridId: grid.id, widget: w, documentActions: documentActions, reloadGridData: reloadGridData, saveSlicer: saveSlicer, tagSlicersData: tagSlicersData, tagSlicersTagIdsLocalChangesHandler: tagSlicersTagIdsLocalChangesHandler })));
        })));
    }
    else {
        return null;
    }
};
var MemoWidgetWrapper = memo(WidgetWrapper, isEqual);
function isEqual(prev, next) {
    if (JSON.stringify(prev.widget) !== JSON.stringify(next.widget)) {
        return false;
    }
    if (JSON.stringify(prev.documentActions) !== JSON.stringify(next.documentActions)) {
        return false;
    }
    if (JSON.stringify(prev.tagSlicersData) !== JSON.stringify(next.tagSlicersData)) {
        return false;
    }
    return true;
}
function WidgetWrapper(props) {
    switch (props.widget.type) {
        case WidgetType.KPI:
            return React.createElement(KPI, __assign({}, props));
        case WidgetType.CHART:
            return React.createElement(Chart, __assign({}, props));
        case WidgetType.SINGLEVALUE:
            return React.createElement(SingleValue, __assign({}, props));
        case WidgetType.SLICER:
            return React.createElement(TimelineSlicer, __assign({}, props));
        case WidgetType.TAGSLICER:
            return React.createElement(TagSlicer, __assign({}, props));
        case WidgetType.SIMULATION:
            return React.createElement(DataSimulation, __assign({}, props));
        case WidgetType.DATAEDITOR:
            return React.createElement(DataEditor, __assign({}, props));
        case WidgetType.TEXTTILE:
            return React.createElement(TextTile, __assign({}, props));
        case WidgetType.PIVOT:
            return React.createElement(Pivot, __assign({}, props));
        default:
            return null;
    }
}
export default DashGrid;
